import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { ChevronDown, MapPin, Hash, Globe, BarChart3, FileSpreadsheet, ArrowRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Product } from '@/hooks/useProducts';
import { getTranslatedField } from '@/lib/translationHelpers';
import { ProductInfoSections } from '@/components/shop/ProductInfoSections';

const categoryIcons: Record<string, React.ElementType> = {
  addresses: MapPin,
  postalCodes: Hash,
  geodata: Globe,
  statistics: BarChart3,
};

interface FileCardProps {
  product: Product;
}

export function FileCard({ product }: FileCardProps) {
  const { t } = useTranslation();
  const { lang = 'sv' } = useParams<{ lang: string }>();
  const [isOpen, setIsOpen] = useState(false);

  const Icon = categoryIcons[product.ws_category] || FileSpreadsheet;


  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border border-border rounded-lg bg-card overflow-hidden transition-shadow hover:shadow-md">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center gap-4 p-4 text-left">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-secondary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-foreground text-sm sm:text-base">
                {getTranslatedField(product, 'name', lang)}
              </h2>
              {product.sku && (
                <span className="text-xs text-muted-foreground">Art.nr: {product.sku}</span>
              )}
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                {getTranslatedField(product, 'short_description', lang)}
              </p>
            </div>
            <ChevronDown 
              className={cn(
                "w-5 h-5 text-muted-foreground transition-transform flex-shrink-0",
                isOpen && "rotate-180"
              )} 
            />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 border-t border-border">
            <div className="pt-4 space-y-4">
              {/* Full description */}
              {product.full_description && (
                <p className="text-muted-foreground">
                  {getTranslatedField(product, 'full_description', lang)}
                </p>
              )}

              {/* Specifications row */}
              <div className="flex flex-wrap justify-between gap-y-3">
                {/* Available countries */}
                {product.available_countries && product.available_countries.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.availableCountries')}
                    </p>
                    <div className="flex gap-2 items-center">
                      {product.available_countries.map((code) => {
                        const flag: Record<string, string> = { SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮' };
                        const name: Record<string, string> = { SE: 'Sverige', NO: 'Norge', DK: 'Danmark', FI: 'Finland' };
                        const upper = code.toUpperCase();
                        if (!flag[upper]) return null;
                        return (
                          <span key={upper} className="inline-flex items-center gap-1 text-sm" title={name[upper]}>
                            <span className="text-lg">{flag[upper]}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Row count */}
                {product.row_count && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.rows')}
                    </p>
                    <p className="font-medium text-foreground">{product.row_count}</p>
                  </div>
                )}

                {/* File formats */}
                {product.file_format && product.file_format.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.formats')}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {product.file_format.map((format) => (
                        <Badge key={format} variant="secondary" className="text-xs">
                          {format}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Encoding (only for non-coordinate products) */}
                {product.encoding && product.spec_type !== 'coordinate_system' && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.encoding')}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      {product.encoding?.join(', ')}
                    </Badge>
                  </div>
                )}

                {/* Coordinate systems (for geodata) */}
                {product.coordinate_systems && product.coordinate_systems.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.coordinateSystems')}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {product.coordinate_systems.map((system) => (
                        <Badge key={system} variant="outline" className="text-xs">
                          {system}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

              </div>

              {/* Shared info sections (collapsibles: file contents, range types, column types) */}
              <ProductInfoSections product={product} showCountries={false} showExamples={false} />


              {/* CTA - links to specific product page */}
              <div className="pt-2">
                <Button asChild size="sm">
                  <Link to={`/${lang}/shop/${product.slug}`}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    {t('dataFiles.orderInShop')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
